import { ApiError, DiscordApi, DiscordInfoResponse, Token } from "@devour/client";
import getConfig from "@/utils/getConfig";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { addError } from "@/redux/meta/metaActions";
import { useDispatch } from "react-redux";

/**
 * Handles Discord authentication using a popup window instead of full page redirect.
 * @param fullToken - The authentication token.
 * @param onSuccess - Optional callback when authentication succeeds
 * @param onError - Optional callback when authentication fails
 */
export async function handleDiscordAuthenticate(
    fullToken: Token,
    onSuccess?: () => void,
    onError?: (error: string) => void,
    returnUrl?: string, // Optional: specify where to return after auth
): Promise<void> {
    try {
        // Always use /discord-callback — a lightweight page that sends postMessage and closes
        const currentReturnUrl = returnUrl || "/discord-callback";

        const data = await new DiscordApi(getConfig(fullToken)).getLinkDiscord({returnUrl: currentReturnUrl});

        if (data && data.url) {
            // Open Discord auth in popup window
            const popup = window.open(
                data.url,
                "discord-auth",
                "width=500,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no",
            );

            if (!popup) {
                throw new Error("Popup blocked. Please allow popups for Discord authentication.");
            }

            let resolved = false;

            /*
             * Listen for postMessage from popup (fast path — works when
             * DiscordCallbackPage is deployed on the same origin).
             */
            const handleMessage = (event: MessageEvent) => {
                // Only accept messages from our own origin — blocks spoofing by third-party iframes/tabs
                if (event.origin !== window.location.origin) return;
                if (typeof event.data !== "object" || event.data === null) {
                    return;
                }

                if (event.data.type === "DISCORD_AUTH_SUCCESS") {
                    resolved = true;
                    cleanup();
                    popup.close();
                    onSuccess?.();
                } else if (event.data.type === "DISCORD_AUTH_ERROR") {
                    resolved = true;
                    cleanup();
                    popup.close();
                    onError?.(typeof event.data.error === "string" ? event.data.error : "Authentication failed");
                }
            };

            // Centralized cleanup to prevent memory leaks
            const cleanup = () => {
                clearInterval(pollPopup);
                clearInterval(apiPoll);
                clearTimeout(timeoutId);
                window.removeEventListener("message", handleMessage);
            };

            window.addEventListener("message", handleMessage);

            /*
             * Poll popup closed state — if user manually closes, do a final
             * API check to see whether the link actually succeeded before
             * reporting success. Without this, errors like
             * "discord_already_linked" were silently swallowed.
             */
            const pollPopup = setInterval(async () => {
                if (popup.closed && !resolved) {
                    resolved = true;
                    cleanup();
                    try {
                        const discordInfo = await new DiscordApi(getConfig(fullToken)).getDiscordAccount();
                        if (discordInfo?.discord?.id) {
                            onSuccess?.();
                        }
                    } catch {
                        /*
                         * API check failed — don't report success or error,
                         * the user simply closed the popup
                         */
                    }
                }
            }, 1000);

            /*
             * Poll the Discord API to detect when linking succeeds.
             * This is the primary detection mechanism — it works regardless
             * of cross-origin issues, missing callback pages, or lost
             * window.opener references. The backend links the account before
             * redirecting, so the API will show the account as linked once
             * the OAuth flow completes on the backend side.
             */
            const apiPoll = setInterval(async () => {
                if (resolved) return;
                try {
                    const discordInfo = await new DiscordApi(getConfig(fullToken)).getDiscordAccount();
                    if (discordInfo?.discord?.id) {
                        resolved = true;
                        cleanup();
                        popup.close();
                        onSuccess?.();
                    }
                } catch {
                    // Not linked yet or network error — keep polling
                }
            }, 3000);

            // Timeout after 5 minutes
            const timeoutId = setTimeout(() => {
                if (!resolved && !popup.closed) {
                    resolved = true;
                    cleanup();
                    popup.close();
                    onError?.("Authentication timed out");
                }
            }, 300000);
        } else {
            throw new Error("Could not get Discord authentication URL");
        }
    } catch (error) {
        console.error("Discord authentication error:", error);
        onError?.(error instanceof Error ? error.message : "Authentication failed");
    }
}

async function fetchDiscordAccount(): Promise<DiscordInfoResponse> {
    return await new DiscordApi(getConfig()).getDiscordAccount();
}

function fetchDiscordAccountQuery() {
    return {
        queryKey: [
            "get-discord-account",
        ],
        queryFn: fetchDiscordAccount,
        placeholderData: keepPreviousData,
        staleTime: 30 * 60000,
        refetchOnWindowFocus: false,
    } as const;
}

export function useGetDiscordAccount(): { data: DiscordInfoResponse, refetch: () => void, isLoading: boolean } {
    return useQuery(fetchDiscordAccountQuery());
}

export function useDisconnectDiscordAccount(fullToken: Token) {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await new DiscordApi(getConfig(fullToken)).createDiscordAccountDisconnect();
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["get-discord-account"] });
        },
        onError: async (error) => dispatch(await addError(error as unknown as ApiError)),
    });
}
